
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cost: number;
  model?: string;
  operationType?: 'completion' | 'embedding' | 'fine-tuning' | 'image-generation';
}

export const useTokenTracking = () => {
  const [tokenUsage, setTokenUsage] = useState<Record<string, TokenUsage>>({});
  const [isRecording, setIsRecording] = useState(false);

  const recordTokenUsage = async (agentId: string, usage: TokenUsage) => {
    try {
      setIsRecording(true);
      
      // Update local state
      setTokenUsage(prev => ({
        ...prev,
        [agentId]: {
          promptTokens: (prev[agentId]?.promptTokens || 0) + usage.promptTokens,
          completionTokens: (prev[agentId]?.completionTokens || 0) + usage.completionTokens,
          totalTokens: (prev[agentId]?.totalTokens || 0) + usage.totalTokens,
          cost: (prev[agentId]?.cost || 0) + usage.cost,
          model: usage.model || prev[agentId]?.model,
          operationType: usage.operationType || 'completion'
        }
      }));
      
      // Record to database via edge function
      const { error } = await supabase.functions.invoke('track-token-usage', {
        body: {
          agentId,
          promptTokens: usage.promptTokens,
          completionTokens: usage.completionTokens,
          totalTokens: usage.totalTokens,
          cost: usage.cost,
          model: usage.model,
          operationType: usage.operationType
        }
      });

      if (error) {
        console.error('Error recording token usage:', error);
      }
    } catch (error) {
      console.error('Error in recordTokenUsage:', error);
    } finally {
      setIsRecording(false);
    }
  };

  const getTotalUsage = (): TokenUsage => {
    const agents = Object.values(tokenUsage);
    return {
      promptTokens: agents.reduce((total, agent) => total + agent.promptTokens, 0),
      completionTokens: agents.reduce((total, agent) => total + agent.completionTokens, 0),
      totalTokens: agents.reduce((total, agent) => total + agent.totalTokens, 0),
      cost: agents.reduce((total, agent) => total + agent.cost, 0)
    };
  };

  return {
    tokenUsage,
    totalUsage: getTotalUsage(),
    recordTokenUsage,
    isRecording
  };
};
